// services/campaignService.js
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import app from '../config/firebase';

class CampaignService {
  constructor() {
    this.functions = getFunctions(app);

    // Connect to emulator if in development
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
      const emulatorHost = process.env.REACT_APP_FIREBASE_EMULATOR_HOST || 'localhost';
      const emulatorPort = process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001;
      connectFunctionsEmulator(this.functions, emulatorHost, emulatorPort);
      console.log(`CampaignService: Connected to Functions Emulator at ${emulatorHost}:${emulatorPort}`);
    }

    // Initialize Firebase function references
    this.interpretTargetAudienceFunc = httpsCallable(this.functions, 'interpretTargetAudience');
    this.executeTargetAudienceQueryFunc = httpsCallable(this.functions, 'executeTargetAudienceQuery');

    // Simulation mode flag (can be toggled for testing)
    this.simulationMode = false;
  }

  /**
   * Enable simulation mode
   */
  enableSimulationMode() {
    this.simulationMode = true;
    console.log('CampaignService: Simulation mode ENABLED');
  }

  /**
   * Disable simulation mode
   */
  disableSimulationMode() {
    this.simulationMode = false;
    console.log('CampaignService: Simulation mode DISABLED');
  }

  /**
   * Check if simulation mode is enabled
   */
  isSimulationMode() {
    return this.simulationMode;
  }

  /**
   * Interpret target audience description using AI (only gets query + interpretation)
   * @param {string} description - Natural language description of target audience
   * @returns {Promise<Object>} Object with interpretation and query only
   */
  async interpretTargetAudience(description) {
    try {
      if (!description || typeof description !== 'string') {
        throw new Error('Description is required and must be a string');
      }

      if (description.length > 500) {
        throw new Error('Description too long. Maximum 500 characters.');
      }

      // Check if simulation mode is enabled
      if (this.simulationMode) {
        console.log('CampaignService: SIMULATION MODE - Returning mock response');

        // Return simulated response
        const simulatedResult = {
          success: true,
          query: "SELECT * FROM customer_stats WHERE full_name LIKE '%חגי%' OR full_name LIKE '%נעמה%' ORDER BY full_name ASC LIMIT 600",
          interpretation: "מוצא לקוחות עם השם חגי או נעמה",
          originalDescription: description,
          timestamp: new Date().toISOString()
        };

        return {
          success: true,
          sqlQuery: simulatedResult.query,
          aiInterpretation: simulatedResult.interpretation,
          originalDescription: simulatedResult.originalDescription,
          timestamp: simulatedResult.timestamp
        };
      }

      console.log('CampaignService: Calling interpretTargetAudience function with:', description);

      // Call the Firebase function that returns only interpretation + query
      // Response structure: { success: true, query: "...", interpretation: "...", originalDescription: "...", timestamp: "..." }
      const result = await this.interpretTargetAudienceFunc({ description });

      console.log('CampaignService: Received interpretation response:', result.data);

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to interpret target audience');
      }

      return {
        success: true,
        sqlQuery: result.data.query,
        aiInterpretation: result.data.interpretation,
        originalDescription: result.data.originalDescription || description,
        timestamp: result.data.timestamp || new Date().toISOString()
      };

    } catch (error) {
      console.error('CampaignService - interpretTargetAudience error:', error);

      return {
        success: false,
        error: error.message || 'Failed to interpret target audience',
        sqlQuery: '',
        aiInterpretation: '',
        originalDescription: description,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Combined function: Interpret + Execute in sequence 
   * @param {string} description - Natural language description of target audience
   * @returns {Promise<Object>} Object with interpretation, query, and customer results
   */
  async interpretAndExecuteTargetAudience(description) {
    try {
      // Step 1: Get AI interpretation and SQL query
      const interpretationResult = await this.interpretTargetAudience(description);
      
      if (!interpretationResult.success) {
        return interpretationResult; // Return the error
      }

      // Step 2: Execute the generated SQL query to get customers
      const executionResult = await this.executeStoredQuery(interpretationResult.sqlQuery);
      
      // Combine both results
      return {
        success: true,
        sqlQuery: interpretationResult.sqlQuery,
        aiInterpretation: interpretationResult.aiInterpretation,
        originalDescription: interpretationResult.originalDescription,
        customers: executionResult.success ? executionResult.customers : [],
        customerCount: executionResult.success ? executionResult.customerCount : 0,
        timestamp: interpretationResult.timestamp,
        executionError: !executionResult.success ? executionResult.error : null
      };

    } catch (error) {
      console.error('CampaignService - interpretAndExecuteTargetAudience error:', error);
      
      return {
        success: false,
        error: error.message || 'Failed to interpret target audience',
        sqlQuery: '',
        aiInterpretation: '',
        originalDescription: description,
        customers: [],
        customerCount: 0,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Execute a stored SQL query to get fresh customer data
   * @param {string} sqlQuery - SQL query to execute
   * @returns {Promise<Object>} Object with customer results
   */
  async executeStoredQuery(sqlQuery) {
    try {
      // Handle empty query (manual selection case)
      if (!sqlQuery) {
        console.log('CampaignService: Empty query detected - skipping execution (manual selection)');
        return {
          success: true,
          customers: [],
          customerCount: 0,
          executedAt: new Date().toISOString(),
          isManualSelection: true
        };
      }

      if (typeof sqlQuery !== 'string') {
        throw new Error('SQL query must be a string');
      }

      console.log('CampaignService: Executing stored query:', sqlQuery);

      const result = await this.executeTargetAudienceQueryFunc({ query: sqlQuery });

      console.log('CampaignService: Query execution response:', result.data);

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to execute query');
      }

      return {
        success: true,
        customers: result.data.customers || [],
        customerCount: result.data.count || 0,
        executedAt: result.data.timestamp || new Date().toISOString()
      };

    } catch (error) {
      console.error('CampaignService - executeStoredQuery error:', error);

      return {
        success: false,
        error: error.message || 'Failed to execute query',
        customers: [],
        customerCount: 0,
        executedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Test a SQL query without storing results
   * @param {string} sqlQuery - SQL query to test
   * @returns {Promise<Object>} Test result
   */
  async testQuery(sqlQuery) {
    try {
      // Add LIMIT 1 to the query for testing
      const testQuery = sqlQuery.replace(/LIMIT \d+/i, 'LIMIT 1');

      const result = await this.executeTargetAudienceQueryFunc({ query: testQuery });

      return {
        success: result.data.success,
        error: result.data.error || null,
        isValid: result.data.success
      };

    } catch (error) {
      return {
        success: false,
        error: error.message || 'Query test failed',
        isValid: false
      };
    }
  }

  /**
   * Validate target audience description
   * @param {string} description - Description to validate
   * @returns {Object} Validation result
   */
  validateTargetAudienceDescription(description) {
    const errors = [];

    if (!description || typeof description !== 'string') {
      errors.push('Target audience description is required');
    } else {
      if (description.trim().length === 0) {
        errors.push('Target audience description cannot be empty');
      }
      
      if (description.length > 500) {
        errors.push('Target audience description must be less than 500 characters');
      }
      
      if (description.length < 10) {
        errors.push('Target audience description should be at least 10 characters');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get suggested target audience templates
   * @returns {Array} Array of template suggestions
   */
  getTargetAudienceTemplates() {
    return [
      {
        label: 'Clients who haven\'t booked recently',
        description: 'customers who haven\'t booked in the last 3 months',
        category: 'Retention'
      },
      {
        label: 'High-value customers',
        description: 'customers who have spent more than $1000',
        category: 'VIP'
      },
      {
        label: 'New customers',
        description: 'customers who registered in the last 30 days',
        category: 'Acquisition'
      },
      {
        label: 'Birthday customers',
        description: 'customers whose birthday is this month',
        category: 'Special Events'
      },
      {
        label: 'Customers with specific skin type',
        description: 'customers with dry skin type',
        category: 'Service-based'
      },
      {
        label: 'WhatsApp preferences',
        description: 'customers who prefer WhatsApp contact',
        category: 'Communication'
      },
      {
        label: 'Frequent customers',
        description: 'customers who book more than twice a month',
        category: 'Loyalty'
      },
      {
        label: 'Customers with cancellations',
        description: 'customers who have cancelled appointments in the last month',
        category: 'Re-engagement'
      }
    ];
  }

  /**
   * Format customer data for display
   * @param {Array} customers - Array of customer objects
   * @returns {Array} Formatted customer data
   */
  formatCustomersForDisplay(customers) {
    return customers.map(customer => ({
      id: customer.id,
      name: customer.full_name,
      phone: customer.phone,
      email: customer.email,
      skinType: customer.skin_type,
      preferredContact: customer.preferred_contact,
      totalSpent: parseFloat(customer.total_spent || 0),
      totalAppointments: parseInt(customer.total_appointments || 0),
      lastAppointmentDate: customer.last_appointment_date,
      daysSinceLastAppointment: parseInt(customer.days_since_last_appointment || 0),
      formattedTotalSpent: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(parseFloat(customer.total_spent || 0)),
      formattedLastAppointment: customer.last_appointment_date 
        ? new Date(customer.last_appointment_date).toLocaleDateString()
        : 'Never'
    }));
  }
}

// Create singleton instance
const campaignService = new CampaignService();

export default campaignService;